/**
 * Shared shell for /dashboard/* sub-pages.
 *
 * Top bar with a prominent "← Back to Dashboard" affordance (Apple HIG
 * 44px hit target, iOS-native pattern) + a centered page title eyebrow
 * + a placeholder right-slot so the layout stays balanced. Content
 * children render below in a `max-w-[1400px]` column.
 *
 * Every sub-page (transactions, creators, copytraders) wraps its body
 * in this shell so the back-to-dashboard path is consistent — one tap,
 * one location, same hit target everywhere.
 */
"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";

export function DashboardSubpageShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0">
          {/* Top bar — back-to-dashboard CTA + page eyebrow.
           * Sticky at top of main content so the dismiss is always
           * reachable as the user scrolls long lists (transactions,
           * creators, copytraders). */}
          <div
            className="sticky top-0 z-20 -mx-5 mb-6 flex items-center justify-between border-b px-5 py-3"
            style={{
              background: "var(--color-bg)",
              borderColor: "var(--color-border)",
            }}
          >
            <Link
              href="/home"
              className="-ml-1 flex h-11 items-center gap-1.5 rounded-lg px-3 text-[14px] font-semibold transition-colors hover:bg-[var(--color-surface2)]"
              style={{ color: "var(--color-text)" }}
              aria-label="Back to Dashboard"
            >
              <ArrowLeft size={18} strokeWidth={2} aria-hidden="true" />
              <span>Back to Dashboard</span>
            </Link>
            <span
              className="hidden font-mono text-[10px] font-semibold uppercase tracking-[0.14em] sm:inline"
              style={{ color: "var(--color-text3)" }}
            >
              {title}
            </span>
            <span className="w-11" aria-hidden="true" />
          </div>

          <header className="mb-6">
            <h1 className="text-[22px] font-bold tracking-tight sm:text-[28px]">
              {title}
            </h1>
            {subtitle && (
              <p
                className="mt-1 text-[13px]"
                style={{ color: "var(--color-text2)" }}
              >
                {subtitle}
              </p>
            )}
          </header>

          {children}
        </main>
      </div>
    </>
  );
}
